import PrimaryButton from '@/components/Buttons/PrimaryButton';
import AttachmentRenderer from '@/components/evaluation/AttachmentRenderer';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Answer, Evaluation, Question as QuestionType } from '@/types';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { router } from '@inertiajs/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface QuestionProps {
    evaluation: Evaluation;
    question: QuestionType;
    attempt: {
        id: number;
        time_remaining_seconds: number | null;
        started_at: string;
    };
    current_question_index: number;
    total_questions: number;
    selected_answer_ids: number[];
    cloze_responses?: string[];
    response_data?: string[];
    is_last_question: boolean;
    course: {
        slug: string;
    } | null;
    isPlacement?: boolean;
}

const QuestionContent: React.FC<QuestionProps> = ({
    evaluation,
    question,
    attempt,
    current_question_index,
    total_questions,
    selected_answer_ids,
    cloze_responses,
    response_data,
    is_last_question,
    course,
    isPlacement = false,
}) => {
    const { t } = useTranslation();
    const basePath = isPlacement ? '/placement-test' : '/evaluations';
    const isCloze = question.question_type === 'cloze';
    const isSort = question.question_type === 'sort';
    const isMatrix = question.question_type === 'matrix';
    const clozeBlankCount = (question.cloze_segments || []).filter(
        (segment) => 'blank' in segment
    ).length;
    const sortChoices = question.sort_items || [];
    const matrixLefts = question.matrix?.lefts || [];
    const matrixOptions = question.matrix?.options || [];
    const allowMultipleAnswers = Boolean(question.allow_multiple_answers);
    const [selectedAnswerIds, setSelectedAnswerIds] = useState<number[]>(
        selected_answer_ids || []
    );
    const [clozeResponses, setClozeResponses] = useState<string[]>(() =>
        Array.from(
            { length: clozeBlankCount },
            (_, i) => (cloze_responses || [])[i] ?? ''
        )
    );
    const [sortOrder, setSortOrder] = useState<string[]>(() =>
        (response_data || []).length > 0 ? [...(response_data as string[])] : [...sortChoices]
    );
    const [matrixSelections, setMatrixSelections] = useState<string[]>(() =>
        Array.from(
            { length: matrixLefts.length },
            (_, i) => (response_data || [])[i] ?? ''
        )
    );
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<number | null>(() => {
        if (!evaluation.time_limit_minutes) {
            return null;
        }
        // Resume from the server's remaining time; fall back to the full limit.
        return attempt.time_remaining_seconds ?? evaluation.time_limit_minutes * 60;
    });
    const questionNumber = current_question_index + 1;

    useEffect(() => {
        setSelectedAnswerIds(selected_answer_ids || []);
        setClozeResponses(
            Array.from(
                { length: clozeBlankCount },
                (_, i) => (cloze_responses || [])[i] ?? ''
            )
        );
        setSortOrder(
            (response_data || []).length > 0
                ? [...(response_data as string[])]
                : [...sortChoices]
        );
        setMatrixSelections(
            Array.from(
                { length: matrixLefts.length },
                (_, i) => (response_data || [])[i] ?? ''
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question.id, selected_answer_ids, cloze_responses, response_data]);
    const pageTitle = `Pregunta ${questionNumber} - ${evaluation.title}`;

    const clozeComplete =
        isCloze &&
        clozeResponses.length === clozeBlankCount &&
        clozeResponses.every((response) => response.trim().length > 0);

    const matrixComplete =
        isMatrix &&
        matrixSelections.length === matrixLefts.length &&
        matrixSelections.every((value) => value.trim().length > 0);

    const handleClozeChange = (index: number, value: string) => {
        setClozeResponses((previous) => {
            const next = [...previous];
            next[index] = value;
            return next;
        });
    };

    const handleMatrixChange = (index: number, value: string) => {
        setMatrixSelections((previous) => {
            const next = [...previous];
            next[index] = value;
            return next;
        });
    };

    const moveSortItem = (from: number, to: number) => {
        if (to < 0 || to >= sortOrder.length || from === to) return;
        setSortOrder((previous) => {
            const next = [...previous];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    useEffect(() => {
        if (!evaluation.time_limit_minutes) return;

        const timer = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev === null || prev <= 0) {
                    // Time's up - redirect to time up page
                    router.get(`/evaluations/${evaluation.id}/time-up/`);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [evaluation.id, evaluation.time_limit_minutes]);

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const getTimerColor = () => {
        if (!timeRemaining || !evaluation.time_limit_minutes)
            return 'text-gray-600';
        const totalSeconds = evaluation.time_limit_minutes * 60;
        const percentage = (timeRemaining / totalSeconds) * 100;

        if (percentage <= 10) return 'text-red-600';
        if (percentage <= 25) return 'text-yellow-600';
        return 'text-green-600';
    };

    const handleAnswerSelect = (answerId: number) => {
        if (!allowMultipleAnswers) {
            setSelectedAnswerIds([answerId]);
            return;
        }

        setSelectedAnswerIds((previous) => {
            if (previous.includes(answerId)) {
                return previous.filter((id) => id !== answerId);
            }
            return [...previous, answerId];
        });
    };

    const handleNext = () => {
        if (isCloze) {
            if (!clozeComplete) {
                alert('Por favor completa el espacio en blanco antes de continuar');
                return;
            }
            router.post(
                `${basePath}/${evaluation.id}/questions/${current_question_index}/`,
                {
                    cloze_responses: clozeResponses,
                }
            );
            return;
        }

        if (isSort) {
            router.post(
                `${basePath}/${evaluation.id}/questions/${current_question_index}/`,
                {
                    response_data: sortOrder,
                }
            );
            return;
        }

        if (isMatrix) {
            if (!matrixComplete) {
                alert('Por favor relaciona todos los elementos antes de continuar');
                return;
            }
            router.post(
                `${basePath}/${evaluation.id}/questions/${current_question_index}/`,
                {
                    response_data: matrixSelections,
                }
            );
            return;
        }

        if (selectedAnswerIds.length === 0) {
            alert('Por favor selecciona una respuesta antes de continuar');
            return;
        }

        // Submit form data using Inertia POST
        router.post(
            `${basePath}/${evaluation.id}/questions/${current_question_index}/`,
            {
                answer_ids: selectedAnswerIds,
            }
        );
    };

    return (
        <AppLayout>
            <PageTitle title={pageTitle} />
            <div className="min-h-screen">
                {/* Header with progress */}
                <div className="border-b border-white/50 bg-white/30 backdrop-blur-sm dark:border-white/10 dark:bg-white/10">
                    <div className="mx-auto max-w-4xl px-4 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-lg font-semibold text-gray-800 dark:text-white">
                                    {evaluation.title}
                                </h1>
                                <p className="text-sm text-gray-600 dark:text-neutral-300">
                                    {t('evaluation.question.questionProgress', {
                                        current: current_question_index + 1,
                                        total: total_questions,
                                    })}
                                </p>
                            </div>

                            {/* Timer */}
                            {evaluation.time_limit_minutes &&
                                timeRemaining !== null && (
                                    <div className="text-right">
                                        <div
                                            className={`text-2xl font-bold ${getTimerColor()}`}
                                        >
                                            {formatTime(timeRemaining)}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-neutral-400">
                                            {t(
                                                'evaluation.question.timeRemaining'
                                            )}
                                        </p>
                                    </div>
                                )}
                        </div>

                        {/* Progress bar */}
                        <div className="mt-4">
                            <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-neutral-700">
                                <div
                                    className="bg-theme-primary h-2 rounded-full transition-all duration-300"
                                    style={{
                                        width: `${((current_question_index + 1) / total_questions) * 100}%`,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Question content */}
                <div className="mx-auto max-w-4xl px-4 py-8">
                    <GlassCard className="p-8">
                        {/* Question */}
                        <div className="mb-8">
                            <h2
                                className="mb-4 text-xl font-semibold text-gray-800 dark:text-white"
                                dangerouslySetInnerHTML={{
                                    __html: cleanWordPressHtml(
                                        question.question
                                    ),
                                }}
                            />

                            {/* Question attachments */}
                            <AttachmentRenderer
                                imageUrl={question.image_url}
                                attachmentUrl={question.attachment_url}
                                altText={t('evaluation.question.questionImage')}
                            />
                        </div>

                        {/* Cloze (fill-in-the-blank): render the sentence with inputs */}
                        {isCloze ? (
                            <div className="mb-8">
                                <p className="mb-4 text-sm text-gray-600 dark:text-neutral-300">
                                    {t('evaluation.question.fillBlank')}
                                </p>
                                <div className="text-lg leading-loose text-gray-800 dark:text-neutral-100">
                                    {(question.cloze_segments || []).map(
                                        (segment, idx) =>
                                            'blank' in segment ? (
                                                <input
                                                    key={`blank-${segment.blank.index}`}
                                                    type="text"
                                                    autoComplete="off"
                                                    value={
                                                        clozeResponses[
                                                            segment.blank.index
                                                        ] ?? ''
                                                    }
                                                    onChange={(e) =>
                                                        handleClozeChange(
                                                            segment.blank.index,
                                                            e.target.value
                                                        )
                                                    }
                                                    className="border-theme-primary mx-1 inline-block w-40 border-0 border-b-2 bg-transparent px-1 py-0.5 text-center focus:outline-none focus:ring-0 dark:text-white"
                                                />
                                            ) : (
                                                <span key={`text-${idx}`}>
                                                    {cleanWordPressHtml(
                                                        segment.text
                                                    )}
                                                </span>
                                            )
                                    )}
                                </div>
                            </div>
                        ) : isSort ? (
                            <div className="mb-8">
                                <p className="mb-4 text-sm text-gray-600 dark:text-neutral-300">
                                    {t('evaluation.question.reorderInstruction')}
                                </p>
                                <div className="space-y-2">
                                    {sortOrder.map((item, index) => (
                                        <div
                                            key={`${item}-${index}`}
                                            draggable
                                            onDragStart={() =>
                                                setDragIndex(index)
                                            }
                                            onDragOver={(e) =>
                                                e.preventDefault()
                                            }
                                            onDrop={() => {
                                                if (dragIndex !== null) {
                                                    moveSortItem(
                                                        dragIndex,
                                                        index
                                                    );
                                                }
                                                setDragIndex(null);
                                            }}
                                            className="flex cursor-move items-center gap-3 rounded-lg border-2 border-gray-200 bg-white/50 p-3 dark:border-neutral-700 dark:bg-white/5"
                                        >
                                            <span className="select-none text-gray-400">
                                                ⠿
                                            </span>
                                            <span className="flex-1 text-gray-800 dark:text-neutral-100">
                                                {cleanWordPressHtml(item)}
                                            </span>
                                            <div className="flex flex-col">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        moveSortItem(
                                                            index,
                                                            index - 1
                                                        )
                                                    }
                                                    disabled={index === 0}
                                                    aria-label="Subir"
                                                    className="px-1 text-gray-500 disabled:opacity-30"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        moveSortItem(
                                                            index,
                                                            index + 1
                                                        )
                                                    }
                                                    disabled={
                                                        index ===
                                                        sortOrder.length - 1
                                                    }
                                                    aria-label="Bajar"
                                                    className="px-1 text-gray-500 disabled:opacity-30"
                                                >
                                                    ▼
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : isMatrix ? (
                            <div className="mb-8">
                                <p className="mb-4 text-sm text-gray-600 dark:text-neutral-300">
                                    {t('evaluation.question.matchInstruction')}
                                </p>
                                <div className="space-y-3">
                                    {matrixLefts.map((left, index) => (
                                        <div
                                            key={`${left}-${index}`}
                                            className="flex flex-col gap-2 sm:flex-row sm:items-center"
                                        >
                                            <span className="flex-1 text-gray-800 dark:text-neutral-100">
                                                {cleanWordPressHtml(left)}
                                            </span>
                                            <select
                                                value={
                                                    matrixSelections[index] ??
                                                    ''
                                                }
                                                onChange={(e) =>
                                                    handleMatrixChange(
                                                        index,
                                                        e.target.value
                                                    )
                                                }
                                                className="flex-1 rounded-lg border-2 border-gray-200 bg-white p-2 text-gray-800 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                                            >
                                                <option value="">
                                                    {t(
                                                        'evaluation.question.selectOption'
                                                    )}
                                                </option>
                                                {matrixOptions.map(
                                                    (option, optionIndex) => (
                                                        <option
                                                            key={optionIndex}
                                                            value={option}
                                                        >
                                                            {cleanWordPressHtml(
                                                                option
                                                            )}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                        /* Answer options */
                        <div className="mb-8 space-y-3">
                            <p className="text-sm text-gray-600 dark:text-neutral-300">
                                {allowMultipleAnswers
                                    ? t('evaluation.question.selectOneOrMore')
                                    : t('evaluation.question.selectOnlyOne')}
                            </p>
                            {question.answers?.map(
                                (answer: Answer, index: number) => (
                                    <div
                                        key={answer.id}
                                        onClick={() =>
                                            handleAnswerSelect(answer.id)
                                        }
                                        className={`flex cursor-pointer items-start gap-4 rounded-lg border-2 p-4 transition-all duration-200 ${
                                            selectedAnswerIds.includes(answer.id)
                                                ? 'border-theme-primary bg-theme-light'
                                                : 'border-gray-200 dark:border-neutral-700 hover:border-gray-300 dark:hover:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-800'
                                        }`}
                                    >
                                        <div
                                            className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center border-2 ${
                                                selectedAnswerIds.includes(answer.id)
                                                    ? 'border-theme-primary bg-theme-primary'
                                                    : 'border-gray-300'
                                            } ${allowMultipleAnswers ? 'rounded' : 'rounded-full'}`}
                                        >
                                            {selectedAnswerIds.includes(answer.id) && (
                                                <div
                                                    className={`bg-white ${allowMultipleAnswers ? 'h-3 w-3 rounded-sm' : 'h-2 w-2 rounded-full'}`}
                                                />
                                            )}
                                        </div>

                                        <div className="flex-1">
                                            <div className="mb-1 flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-500 dark:text-neutral-400">
                                                    {String.fromCharCode(
                                                        65 + index
                                                    )}
                                                    .
                                                </span>
                                            </div>
                                            <div className="mb-3 leading-relaxed text-gray-800 dark:text-neutral-100">
                                                {cleanWordPressHtml(answer.answer)}
                                            </div>

                                            {/* Answer attachments */}
                                            <AttachmentRenderer
                                                imageUrl={answer.image_url}
                                                attachmentUrl={
                                                    answer.attachment_url
                                                }
                                                altText={t(
                                                    'evaluation.answer.answerImage'
                                                )}
                                            />
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                        )}

                        {/* Next button */}
                        <div className="flex justify-end">
                            <PrimaryButton
                                onClick={handleNext}
                                disabled={
                                    isCloze
                                        ? !clozeComplete
                                        : isMatrix
                                          ? !matrixComplete
                                          : isSort
                                            ? false
                                            : selectedAnswerIds.length === 0
                                }
                                size="md"
                            >
                                {is_last_question
                                    ? t('evaluation.question.finish')
                                    : t('evaluation.question.next')}
                            </PrimaryButton>
                        </div>
                    </GlassCard>
                </div>
            </div>
        </AppLayout>
    );
};

const Question: React.FC<QuestionProps> = (props) => {
    return (
        <ThemeWrapper>
            <QuestionContent {...props} />
        </ThemeWrapper>
    );
};

export default Question;
